import { useCallback } from "react";
import { createSearchParams, generatePath, Link, useNavigate } from "react-router";

import { getImageUrl } from "@/lib/aws-amplify/host-storage";
import { cn } from "@/lib/chillnn/cn";
import { getPlanDetailUrl } from "@/lib/chillnn/get-detail-url";
import { getTodaysDate } from "@/lib/dayjs/get-todays-date";
import { BillingTypeTarget, BillingTypeTerm, SaleTypeForTerm, type Plan } from "@/lib/gql/__generated__/graphql";
import { useCopyToClipboard } from "@/lib/react-use/use-copy-to-clipboard";

import { PATHS } from "@/routes/paths";

import { IconButtonList } from "@/components/features/items/edit/_shared/ui/components/system/list/list-item/list-icon-buttons";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import DragIndicator from "@/components/ui/icon/drag-indicator";
import { Image } from "@/components/ui/image/image";
import { Label } from "@/components/ui/label/label";
import { TableCell } from "@/components/ui/table/table";

type Props = {
	plan: Plan;
	editPlanPagePath: string;

	onTransitionToEdit: () => void;
	onDeletePlan: () => Promise<void>;

	isSorting?: boolean;
	showScConnectedColumn: boolean;
};

export const PlanListPageTableItem = ({
	plan,
	editPlanPagePath,
	isSorting = false,
	onDeletePlan,
	onTransitionToEdit,
	showScConnectedColumn,
}: Props) => {
	const todaysDate = getTodaysDate();
	const { copy } = useCopyToClipboard();

	const isOutOfPeriod =
		plan.saleTypeForTerm === SaleTypeForTerm.Limited &&
		plan.saleStart &&
		plan.saleEnd &&
		!(plan.saleStart <= todaysDate && todaysDate <= plan.saleEnd);

	const isOutOfStock = (plan.maxStockNum ?? -1) >= 0 ? plan.maxStockNum === plan.soldNumTotal : false;

	// 「宿泊を通して」は廃止済みの残データ
	const billingTypeTermText =
		plan.billingTypeTerm === BillingTypeTerm.PerDate
			? plan.isDayTrip
				? "１日ごと"
				: "１泊ごと"
			: "宿泊を通して";
	const billingTypeTargetText =
		plan.billingTypeTarget === BillingTypeTarget.Group
			? "１団体あたり"
			: plan.billingTypeTarget === BillingTypeTarget.Person
				? "１人あたり"
				: plan.billingTypeTarget === BillingTypeTarget.Room
					? "１部屋あたり"
					: "";
	const saleUnitText = billingTypeTermText + billingTypeTargetText;

	const imageURL = getImageUrl(plan.mainKeyVisual);

	const navigate = useNavigate();

	const onClickEdit = useCallback(() => {
		onTransitionToEdit();
	}, [onTransitionToEdit]);

	const onClickCopy = useCallback(() => {
		const createNewItemPagePath = plan.isDayTrip
			? generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.NEW.PLAN.DAYTRIP, { hotelID: plan.hotelID })
			: generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.NEW.PLAN.STANDARD, { hotelID: plan.hotelID });
		const params = createSearchParams({ duplicateItemID: plan.planID });
		/**
		 * @todo: クエリパラメータ（duplicateItemID）の管理方法を定義したい
		 */
		const path = createNewItemPagePath + "?" + params.toString();
		navigate(path);
	}, [navigate, plan.hotelID, plan.isDayTrip, plan.planID]);

	const onClickDelete = useCallback(async () => {
		await onDeletePlan();
	}, [onDeletePlan]);

	const onClickOpen = useCallback(() => {
		const roomDetailURL = getPlanDetailUrl(plan.hotelID, plan.planID);
		window.open(roomDetailURL, "_blank");
	}, [plan.hotelID, plan.planID]);

	const onClickCopyGuestUrl = useCallback(() => {
		const url = getPlanDetailUrl(plan.hotelID, plan.planID);
		copy(url);
		notifyUserEventInfo({
			title: "URLをコピーしました",
		});
	}, [copy, plan.hotelID, plan.planID]);

	return (
		<>
			<TableCell className={cn(!isSorting && "hidden")}>
				<div className="grid place-content-center">
					<div className="shrink-0 rounded p-1 hover:cursor-grab hover:bg-zinc-100">
						<DragIndicator size="default" />
					</div>
				</div>
			</TableCell>
			<TableCell className="flex items-center gap-x-3">
				<Image src={imageURL} className="h-16 w-16 min-w-16 shrink-0 rounded object-cover" />

				<div className={cn("flex flex-col gap-y-1.5", plan.isDayTrip && "self-start")}>
					{plan.isDayTrip ? <Label variant="green" text="日帰り" size="xs" className="w-fit" /> : null}
					<Link to={editPlanPagePath} className="underline-offset-4 hover:underline" viewTransition>
						{plan.name}
					</Link>
				</div>
			</TableCell>
			<TableCell className="text-center">
				<Label variant={plan.inSale ? "green" : "pink"} text={plan.inSale ? "公開" : "非公開"} />
			</TableCell>
			{showScConnectedColumn ? (
				<TableCell className="text-center">
					{plan.isSCConnected ? "連携中" : "未連携"}
				</TableCell>
			) : null}
			<TableCell className="text-center">
				{!isOutOfPeriod && !isOutOfStock ? (
					<p>{plan.maxStockNum ? plan.maxStockNum - (plan.soldNumTotal || 0) : `制限なし`}</p>
				) : (
					<p>{!isOutOfPeriod ? "販売期間外" : !isOutOfStock ? "在庫切れ" : null}</p>
				)}
			</TableCell>
			<TableCell className="text-right">
				<p>{saleUnitText}</p>
				<p>
					<span className="font-semibold">{(plan?.price?.amount ?? 0).toLocaleString()}円</span>
				</p>
			</TableCell>
			<TableCell>
				<div className="flex justify-center">
					<IconButtonList
						onClickCopyButton={onClickCopy}
						onClickDeleteButton={onClickDelete}
						onClickEditButton={onClickEdit}
						onClickOpenButton={onClickOpen}
						onClickCopyGuestUrlButton={plan.isPrivate ? onClickCopyGuestUrl : undefined}
					/>
				</div>
			</TableCell>
		</>
	);
};

PlanListPageTableItem.displayName = "PlanListPageTableItem";
